import React, { useState, useLayoutEffect, useContext, useEffect } from "react";
import { useSelector } from "react-redux";
import DateLib from "../../../inc/DateLib";
import ApiProxy from "../../../app/api/admin/proxy/ApiProxy";
import Page from "../../modules/page";
import { ContextPage } from "../../modules/page/pageContext";
import { ContextContent } from "../../modules/content/contentContext";
import List from "../../modules/list";
import ListHeaderItem from "../../modules/list/__header-item";
import ListBodyItem from "../../modules/list/__body-item";
import { ContextList } from "../../modules/list/listContext";
import useListFilters from "../../modules/list/useListFilters";
import ButtonLinkShow from "../../components/button-link/__show";
import ButtonLinkAdd from "../../components/button-link/__add";
import InputNumber from "../../components/inputs/number/number";
import InputText from "../../components/inputs/text";
import Select from "../../components/inputs/select";
import Button from "../../components/button";
import {
    CHECK_STATUS_OPTIONS, PROXY_POLL_MS, ProxyCheckBadge, ProxyCheckButton, concurrencyTitle, formatConcurrency,
    formatStepOk, isProxyPending,
} from "./check";
import {
    PROTOCOL_OPTIONS, REGION_OPTIONS, PROXY_MODE_UNKNOWN_LABEL, formatProxyAddress, formatProxyExit, proxyModeLabel,
} from "./proxy";
import ProxyImportModal from "./import";
import ProxiesBatchBar, { SelectAllCheckbox } from "./batch";
import InputCheckbox from "../../components/inputs/checkbox";
import "./styles.css";

const IS_ACTIVE_OPTIONS = { '1': 'да', '0': 'нет' };
// Фильтр ?proxy_group_id=none — прокси без группы (ProxyFilter::proxyGroupId).
const NO_GROUP_OPTION = { none: 'Без группы' };

// Список прокси: фильтры и сортировка — по ProxyFilter (сортировка только по id / host /
// ping_ms / last_checked_at / expires_at, остальные колонки без сортировки).
export default function ProxiesIndex({ ...other }) {
    return <Page {...other}>
        <ProxiesIndexContent/>
    </Page>;
}

// Проверка из строки: ставит в очередь и перечитывает список — в строке станет «В очереди»,
// дальше список обновляется сам (ProxyPendingPoller), пока проверка не закончится.
function ProxyCheckCell({ proxy }) {
    const contextList = useContext(ContextList);
    return <ProxyCheckButton proxyId={proxy.id} disabled={isProxyPending({ last_check: proxy.last_check })}
        onDone={() => contextList.reload()}/>;
}

// Перечитывает список, пока на странице есть прокси с проверкой или замером в очереди / в работе.
function ProxyPendingPoller() {
    const contextList = useContext(ContextList);
    const hasPending = (contextList.list ?? []).some(isProxyPending);
    const reload = contextList.reload;

    useEffect(() => {
        if (!hasPending) return undefined;
        const timer = setInterval(reload, PROXY_POLL_MS);
        return () => clearInterval(timer);
    }, [hasPending, reload]);

    return null;
}

// Кнопка «Импорт» в шапке: модалка живёт внутри List — ей нужен contextList.reload(). Стили ячейки
// шапки (заглавные, nowrap) она не наследует: Modal сам рисуется порталом в body.
function ProxyImportHeader() {
    const contextList = useContext(ContextList);
    const [isOpenModal, setIsOpenModal] = useState(false);

    return <>
        <Button className="proxies__import-button" onClick={() => setIsOpenModal(true)}>Импорт</Button>
        {isOpenModal
            ? <ProxyImportModal onClose={() => setIsOpenModal(false)} onDone={() => contextList.reload()}/>
            : null}
    </>;
}

function ProxiesIndexContent() {
    const contextContent = useContext(ContextContent);
    const contextPage = useContext(ContextPage);

    // Фильтры — в URL (useListFilters): переживают «Назад» и перезагрузку, как сортировка.
    const filter = useListFilters(['id', 'host', 'protocol', 'mode', 'status', 'is_active', 'country_code', 'region', 'proxy_group_id']);

    // Группы прокси — справочник app-init {id: подпись}; «Без группы» — последним пунктом фильтра.
    const proxyGroups = useSelector((state) => state.option.proxyGroups);
    const deviceProxyModes = useSelector((state) => state.option.deviceProxyModes);
    // Фильтр режима: оба режима из справочника и «не определён» (mode=none) — массивом, чтобы порядок
    // был как задан.
    const modeFilterOptions = [...Object.entries(deviceProxyModes), ['none', PROXY_MODE_UNKNOWN_LABEL]];
    const groupFilterOptions = { ...proxyGroups, ...NO_GROUP_OPTION };

    // Выбор прокси чекбоксами для массовых действий (ProxiesBatchBar) — как у устройств:
    // «выбрать все» берёт только текущую страницу, выбор переживает смену страницы.
    const [selectedIds, setSelectedIds] = useState([]);
    const toggleSelect = (id, isChecked) => {
        setSelectedIds((previous) => (isChecked ? [...previous, id] : previous.filter((item) => item !== id)));
    };

    useLayoutEffect(() => {
        contextContent.setTitle('Прокси');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const canCheck = contextContent.checkPermission('edit');
    const canBatch = canCheck || contextContent.checkPermission('destroy');

    const header = [
        ...(canBatch ? [
            <ListHeaderItem key="select" name="select" isSort={false} isCardHead={true} isAction={true}>
                {/* Подпись видна только в мобильной полоске действий (в шапке таблицы её прячет CSS). */}
                <SelectAllCheckbox selectedIds={selectedIds} setSelectedIds={setSelectedIds}>Выбрать все</SelectAllCheckbox>
            </ListHeaderItem>,
        ] : []),
        <ListHeaderItem key="id" name="id">ID</ListHeaderItem>,
        <ListHeaderItem key="host" name="host">Адрес</ListHeaderItem>,
        <ListHeaderItem key="protocol" name="protocol" isSort={false}>Протокол</ListHeaderItem>,
        <ListHeaderItem key="mode" name="mode" isSort={false}>Режим</ListHeaderItem>,
        <ListHeaderItem key="username" name="username" isSort={false}>Логин</ListHeaderItem>,
        <ListHeaderItem key="is_active" name="is_active" isSort={false}>Активен</ListHeaderItem>,
        <ListHeaderItem key="proxy_group_id" name="proxy_group_id" isSort={false}>Группа</ListHeaderItem>,
        <ListHeaderItem key="exit" name="exit" isSort={false}>Выход</ListHeaderItem>,
        <ListHeaderItem key="status" name="status" isSort={false}>Проверка</ListHeaderItem>,
        <ListHeaderItem key="ping_ms" name="ping_ms">Пинг, мс</ListHeaderItem>,
        <ListHeaderItem key="tcp_udp" name="tcp_udp" isSort={false}>TCP / UDP</ListHeaderItem>,
        <ListHeaderItem key="concurrency" name="concurrency" isSort={false}>Одновременных соединений</ListHeaderItem>,
        <ListHeaderItem key="last_checked_at" name="last_checked_at">Проверен</ListHeaderItem>,
        <ListHeaderItem key="expires_at" name="expires_at">Оплачен до</ListHeaderItem>,
        <ListHeaderItem key="check" name="check" isSort={false}>
            {contextContent.checkPermission('create') ? <ProxyImportHeader/> : null}
        </ListHeaderItem>,
        <ListHeaderItem key="create" name="create" isSort={false}>
            {contextContent.checkPermission('create')
                ? <ButtonLinkAdd className="button-link_white" title="Добавить" href={contextPage.routes.create.url()}/> : null}
        </ListHeaderItem>,
    ];

    const filters = [
        <InputNumber className="input_wf" value={filter.values.id}
            onChange={(e) => filter.setValue('id', e.target.value)}>id</InputNumber>,
        <InputText className="input_wf" value={filter.values.host}
            onChange={(e) => filter.setValue('host', e.target.value)}>Адрес</InputText>,
        <Select className="input_wf" options={PROTOCOL_OPTIONS} value={filter.values.protocol} isNull={true}
            onChange={(e) => filter.setValue('protocol', e.target.value)}>Протокол</Select>,
        <Select className="input_wf" options={modeFilterOptions} value={filter.values.mode} isNull={true}
            onChange={(e) => filter.setValue('mode', e.target.value)}>Режим</Select>,
        <Select className="input_wf" options={CHECK_STATUS_OPTIONS} value={filter.values.status} isNull={true}
            onChange={(e) => filter.setValue('status', e.target.value)}>Проверка</Select>,
        <Select className="input_wf" options={IS_ACTIVE_OPTIONS} value={filter.values.is_active} isNull={true}
            onChange={(e) => filter.setValue('is_active', e.target.value)}>Активен</Select>,
        <Select className="input_wf" options={groupFilterOptions} value={filter.values.proxy_group_id} isNull={true}
            onChange={(e) => filter.setValue('proxy_group_id', e.target.value)}>Группа</Select>,
        <Select className="input_wf" options={REGION_OPTIONS} value={filter.values.region} isNull={true}
            onChange={(e) => filter.setValue('region', e.target.value)}>Регион выхода</Select>,
        <InputText className="input_wf" value={filter.values.country_code} placeholder="напр. NL"
            onChange={(e) => filter.setValue('country_code', e.target.value)}>Страна выхода</InputText>,
    ];

    const renderRow = (row) => [
        ...(canBatch ? [
            <ListBodyItem key="select">
                <InputCheckbox value={selectedIds.includes(row.id)} onChange={(e) => toggleSelect(row.id, e.target.checked)}/>
            </ListBodyItem>,
        ] : []),
        <ListBodyItem key="id">{row.id}</ListBodyItem>,
        <ListBodyItem key="host">{formatProxyAddress(row.host, row.port)}</ListBodyItem>,
        <ListBodyItem key="protocol">{PROTOCOL_OPTIONS[row.protocol] ?? row.protocol}</ListBodyItem>,
        // Режим: «Только TCP» — жёлтым (устройствам с ним доступен только этот режим), не определён —
        // приглушённо (определит проверка).
        <ListBodyItem key="mode">
            <span className={`proxies__mode${row.mode ? ` proxies__mode_${row.mode}` : ' proxies__mode_unknown'}`}>
                {proxyModeLabel(row.mode, deviceProxyModes)}
            </span>
        </ListBodyItem>,
        <ListBodyItem key="username">{row.username || '—'}</ListBodyItem>,
        <ListBodyItem key="is_active">
            <span className={`badge ${row.is_active ? 'badge_online' : ''}`}>{row.is_active ? 'да' : 'нет'}</span>
        </ListBodyItem>,
        <ListBodyItem key="proxy_group_id">
            {(row.proxy_group_id ?? null) === null ? '—' : (proxyGroups[row.proxy_group_id] ?? `#${row.proxy_group_id}`)}
        </ListBodyItem>,
        <ListBodyItem key="exit">{formatProxyExit(row)}</ListBodyItem>,
        <ListBodyItem key="status">
            <ProxyCheckBadge status={row.last_check.status}/>
            {row.last_check.error ? <div className="proxies__check-error">{row.last_check.error}</div> : null}
        </ListBodyItem>,
        <ListBodyItem key="ping_ms">{row.last_check.ping_ms ?? '—'}</ListBodyItem>,
        <ListBodyItem key="tcp_udp">
            {formatStepOk(row.last_check.tcp_ok)} / {formatStepOk(row.last_check.udp_ok)}
        </ListBodyItem>,
        <ListBodyItem key="concurrency">
            <span title={concurrencyTitle(row.concurrency)}>{formatConcurrency(row.concurrency)}</span>
        </ListBodyItem>,
        <ListBodyItem key="last_checked_at">{DateLib.formatterDateTime(row.last_check.checked_at) || '—'}</ListBodyItem>,
        <ListBodyItem key="expires_at">{row.expires_at ? row.expires_at.split('-').reverse().join('.') : '—'}</ListBodyItem>,
        <ListBodyItem key="check">{canCheck ? <ProxyCheckCell proxy={row}/> : null}</ListBodyItem>,
        <ListBodyItem key="show">
            {contextContent.checkPermission('show')
                ? <ButtonLinkShow className="button-link_blue" title="Просмотр" href={contextPage.routes.show.url({ id: row.id })}/> : null}
        </ListBodyItem>,
    ];

    return <List
        header={header}
        ApiModel={ApiProxy}
        renderRow={renderRow}
        filters={filters}
        defaultSortKey="id"
        defaultSortIsAsc={false}
        selectedIds={selectedIds}
        searchParams={filter.applied}
        onApplyFilters={filter.apply}
        onResetFilters={filter.reset}
    >
        <ProxyPendingPoller/>
        {(canBatch && selectedIds.length > 0) ? <ProxiesBatchBar
            selectedIds={selectedIds}
            onClear={() => setSelectedIds([])}
            onDeleted={(deletedIds) => setSelectedIds((previous) => previous.filter((id) => !deletedIds.includes(id)))}
        /> : null}
    </List>;
}
